import { Bubble } from "@ant-design/x";
import { Card } from "antd";
import styled from "styled-components";

import type { TMessageTone } from "@/modules/Socket/helpers";

export const StyledContainer = styled(Card)<{ $primary?: string }>`
  width: 100%;
  height: 90dvh;
  border-radius: 1.5rem;
  border: 1px solid var(--main-color);
  box-shadow: -0.25rem 0.25rem 0.25rem 0 #0000000D;
  overflow: hidden;
  position: relative;

  .ant-card-head {
    background-color: ${({ $primary }) => $primary || '#E23A00'};
    border: none;
    box-shadow: 0 1px 0 0 #0000001a;
    padding: 0.875rem 1rem;

    button {
      border: none;
    }
  }

  .ant-card-body {
    padding: 0.05rem 0 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    position: relative;
    height: calc(90dvh - 4.5rem);

    @media screen and (min-width: 48em) {
      height: calc(100% - 5.5rem);
    }

    &::-webkit-scrollbar {
      width: 0.25rem;
    }

    &::-webkit-scrollbar-track {
      background: rgb(234, 237, 240);
      border-radius: 0.25rem;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--main-color);
      border-radius: 0.25rem;
    }
  }

  .ant-card-actions {
    display: none;
  }

  .ant-card-actions>li >span {
    cursor: default;
  }
  @media screen and (min-width: 48em) {
    width: 30.25rem;
    height: 45.625rem !important;
  }
`;

export const ContentScrollArea = styled.div`
  flex: 1 1 auto;
  padding: 1.3rem 1rem 1rem;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SenderStickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  background-color: #fff;
  box-shadow: 0 -1px 0 0 #0000001a;
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
  padding: 0.75rem 0.875rem 0rem;
  gap: 0.5rem;
`;

export const StyledBubbleList = styled(Bubble.List)`

  .ant-bubble-end,
  .ant-bubble-start {
    border-radius: 1rem;
    font-size: 1.125rem;
  }

  .ant-bubble-end {
    margin-left: auto;

    .ant-bubble-content {
      background: #f1f3f4;
      border-radius: 1.125rem 1.125rem 0.313rem 1.125rem;
      color: var(--text-color);
    }
  }

  .ant-bubble-start {
    margin-right: auto;

    .ant-bubble-header {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: 0;
    }

    .ant-bubble-content {
      padding: 0;
      margin: 0 0 0.3rem 2.7rem;
      margin-top: -0.6rem;
      background: #ffffff;
    }
  }

  .ant-bubble-start.system-bubble {
    margin-right: 0;
    width: 100%;
  }

  .ant-bubble-start.system-bubble .system-pill-content {
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 0;
    padding: 0;
    background: transparent;
  }

  .ant-bubble-start.system-bubble .system-pill-footer {
    width: 100%;
    text-align: center;
    margin-top: 0.25rem;
  }
` as typeof Bubble.List;

export const StyledWrapper = styled.div`
  position: relative;
`;

export const StyledTime = styled.time`
  font-size: 0.75rem;
  line-height: 1.25;
  color: var(--text-color);
  opacity: 0.5;
`;

export const StyledBotNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(152, 162, 179);
  font-size: 0.656rem;
`

export const StyledExtra = styled.div`
  display: flex;
  align-items: center;
  gap: 0.688rem;
`;

export const DebugCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.313rem;
  color: #ffffff;
  font-size: 0.688rem;
  cursor: pointer;
  user-select: none;
`;

export const HeaderBar = styled.div<{ $primary?: string }>`
  display: flex;
  align-items: center;
  gap: 0.688rem;
  background: ${({ $primary }) => $primary || '#E23A00'};
  color: #ffffff;
`;

export const HeaderAvatar = styled.div<{ $primary?: string }>`
  position: relative;
  flex: none;
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
 
  .anticon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80%;
    height: 80%;
  }

  svg {
    width: 90%;
    height: 90%;
    color: ${({ $primary }) => `color-mix(in srgb, ${$primary} 9%, transparent)`};
  }
`;
 
export const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
 
export const BotName = styled.div`
  font-size: 0.938rem;
  font-weight: 700;
  line-height: 1.2;
`;

export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.313rem;
  font-size: 0.719rem;
  opacity: 0.92;
  margin-top: 0.125rem;
`;

const TONE_COLOR_VAR: Record<TMessageTone, string> = {
  neutral: 'var(--status-neutral-color)',
  success: 'var(--status-success-color)',
  warning: 'var(--status-warning-color)',
  danger: 'var(--status-danger-color)',
};


const TONE_BG_VAR: Record<TMessageTone, string> = {
  neutral: 'color-mix(in srgb, var(--status-neutral-color) 15%, transparent)',
  success: 'color-mix(in srgb, var(--status-success-color) 15%, transparent)',
  warning: 'color-mix(in srgb, var(--status-warning-color) 15%, transparent)',
  danger: 'color-mix(in srgb, var(--status-danger-color) 15%, transparent)',
};

export const StatusDot = styled.span<{ $tone?: TMessageTone }>`
  width: 0.438rem;
  height: 0.438rem;
  border-radius: 50%;
  background: ${({ $tone }) => ($tone ? TONE_COLOR_VAR[$tone] : '#3ddc84')};
  display: inline-block;
  flex-shrink: 0;
`;

export const StyledSystemMessage = styled.div<{ $tone: TMessageTone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 0.875rem;
  border-radius: 999px;
  background: ${({ $tone }) => TONE_BG_VAR[$tone]};
  color: ${({ $tone }) => TONE_COLOR_VAR[$tone]};
  font-size: 0.75rem;
  font-weight: 400;
`;
 
/* ---- Restart / Close icon buttons ---- */
 
export const HeaderIconButton = styled.button`
  flex: none;
  width: 2rem;
  height: 2rem;
  border-radius: 0.563rem;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }

  svg {
    width: 1.063rem;
    height: 1.063rem;
  }

  .ant-btn {
    background: transparent !important;
    border-color: transparent !important;
    color: #ffffff !important;
    box-shadow: none !important;
  }

  .ant-btn:hover,
  .ant-btn:focus {
    background: transparent !important;
    border-color: transparent !important;
    color: #ffffff !important;
  }
`;